/** Primary drawing color (matches indicator accent). */
export const DEFAULT_DRAWING_COLOR = "#2196F3";

/**
 * Fibonacci studies read as structure, not as annotations — they use the brand
 * blue rather than the old off-palette "#666666" grey-on-grey.
 */
export const DEFAULT_FIB_COLOR = "#A3A3A3";

/** Chip background for Fibonacci level tags — the terminal's panel tone. */
export const FIB_TAG_BG = "#0F0F0F";

/** Terminal type stack. UI captions use Inter; prices/ratios use Roboto. */
export const UI_FONT = "Inter, Roboto, ui-sans-serif, system-ui, sans-serif";
export const DATA_FONT = "Roboto, ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * Shared Fibonacci presentation flags.
 * `extendRight` is OFF by default: a study is clipped to its own [t0,t1] on the
 * time axis. Opt in per drawing to project levels forward past the last anchor.
 */
export const DEFAULT_FIB_PRESENTATION = {
  extendRight: false,
  showBands: true,
  showPrices: true,
  anchorEmphasis: true,
};

export const DEFAULT_LINE_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  lineWidth: 1,
  lineStyle: "solid" as const,
};

export const DEFAULT_SHAPE_STYLE = {
  fillColor: "rgba(33, 150, 243, 0.25)",
  borderColor: DEFAULT_DRAWING_COLOR,
  lineWidth: 1,
  lineStyle: "solid" as const,
};

export const DEFAULT_FIBONACCI_STYLE = {
  textColor: DEFAULT_FIB_COLOR,
  fontSize: 10,
  fontFamily: DATA_FONT,
  fontWeight: "normal",
  lineColor: DEFAULT_FIB_COLOR,
  lineWidth: 1,
  lineStyle: "solid",
  ...DEFAULT_FIB_PRESENTATION,
};

export const DEFAULT_PRICE_LABEL_STYLE = {
  fontSize: 11,
  fontFamily: DATA_FONT,
  fontWeight: "normal",
  backgroundColor: DEFAULT_DRAWING_COLOR,
  borderColor: DEFAULT_DRAWING_COLOR,
  // textColor is intentionally omitted — the chip auto-inks for contrast.
};

export const DEFAULT_TEXT_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  fontSize: 13,
  fontFamily: UI_FONT,
  fontWeight: "normal",
  align: "center",
  baseline: "middle",
  backgroundColor: "transparent",
  paddingLeft: 4,
  paddingRight: 4,
  paddingTop: 2,
  paddingBottom: 2,
  borderRadius: 0,
  borderStyle: "solid",
  borderColor: "transparent",
  borderSize: 0,
};

export const DEFAULT_ARROWED_LINE_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  lineWidth: 2,
  lineStyle: "solid",
  arrowSize: 15,
  arrowAngle: Math.PI / 6,
  arrowFilled: true,
};

export const DEFAULT_EQUIDISTANT_CHANNEL_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  lineWidth: 1,
  lineStyle: "solid",
  showBorders: true,
};

export const DEFAULT_ELLIOTT_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  lineWidth: 2,
  lineStyle: "solid",
  showLabels: true,
  labelSize: 11,
  labelOffset: 15,
  showTrendChannel: false,
};

export const DEFAULT_GANN_GRID_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  gridColor: DEFAULT_DRAWING_COLOR,
  lineWidth: 1,
  lineStyle: "solid",
  diagonalDashed: false,
  divisions: 8,
  showFractions: false,
  labelColor: DEFAULT_DRAWING_COLOR,
  labelSize: 10,
  showGannFan: false,
  fanColor: DEFAULT_DRAWING_COLOR,
};

export const FIBONACCI_DEFAULT_LEVELS = [
  { value: 0, visible: true },
  { value: 0.236, visible: true },
  { value: 0.382, visible: true },
  { value: 0.5, visible: true },
  { value: 0.618, visible: true },
  { value: 0.786, visible: true },
  { value: 1, visible: true },
];

export const DEFAULT_TREND_BY_ANGLE_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  lineWidth: 1,
  lineStyle: "solid" as const,
};

export const DEFAULT_CHANNEL_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  lineWidth: 1,
  lineStyle: "solid" as const,
};

export const DEFAULT_FIB_STYLE = {
  lineColor: DEFAULT_FIB_COLOR,
  textColor: DEFAULT_FIB_COLOR,
  lineWidth: 1,
  lineStyle: "solid",
  fontSize: 10,
  fontFamily: DATA_FONT,
  fontWeight: "normal",
  ...DEFAULT_FIB_PRESENTATION,
};

export const DEFAULT_GANN_LINE_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  lineWidth: 1,
  lineStyle: "solid" as const,
};

export const DEFAULT_SIGNAL_ARROW_STYLE = {
  color: "#26a69a",
  lineWidth: 1,
  lineStyle: "solid" as const,
};

export const DEFAULT_TEXT_LABEL_STYLE = {
  color: DEFAULT_DRAWING_COLOR,
  backgroundColor: DEFAULT_DRAWING_COLOR,
  borderColor: DEFAULT_DRAWING_COLOR,
  fontSize: 11,
  fontFamily: UI_FONT,
  fontWeight: "500",
  // textColor is intentionally omitted — the chip auto-inks for contrast.
};
